/**
 * AI Radar feedback triggers for Word Collection.
 *
 * Defines conditions that activate specific types of AI feedback,
 * with priority ordering and cooldown management to prevent spam.
 */

import type { WordCollectionAIContext } from '../../types/wordCollectionAIContext'
import {
  buildRhymeExplorationPrompt,
  buildPatternAnalysisPrompt,
  buildStabilityAlignmentPrompt,
  buildExplorationMomentumPrompt,
  buildCreativeSuggestionsPrompt
} from './wordCollectionPrompts'

/**
 * Feedback trigger definition - when and how to deliver AI feedback
 */
export interface FeedbackTrigger {
  /** Unique identifier */
  id: string
  /** Human-readable name */
  name: string
  /** Priority (1 = highest, 5 = lowest) - determines order when multiple triggers active */
  priority: 1 | 2 | 3 | 4 | 5
  /** Minimum time before re-triggering (milliseconds) */
  cooldownMs: number
  /** Condition function - returns true if trigger should activate */
  condition: (context: WordCollectionAIContext) => boolean
  /** Prompt builder function - generates AI prompt when triggered */
  buildPrompt: (context: WordCollectionAIContext) => string
}

/**
 * Rate limiting configuration
 */
export const FEEDBACK_LIMITS = {
  /** Maximum number of concurrent feedback items */
  maxConcurrentFeedback: 1,
  /** Minimum time between ANY feedback triggers (milliseconds) */
  minTimeBetweenAnyMs: 30000, // 30 seconds
  /** Dismissal persistence duration (milliseconds) */
  dismissalPersistenceMs: 3600000, // 1 hour
}

/**
 * All feedback triggers, ordered by priority
 */
export const WORD_COLLECTION_TRIGGERS: FeedbackTrigger[] = [
  // Priority 1: (none currently - reserved for critical issues)

  // Priority 2: High-value actionable feedback
  {
    id: 'rhyme_exploration',
    name: 'Rhyme Exploration Opportunity',
    priority: 2,
    cooldownMs: 600000, // 10 minutes
    // Enough unexplored words to make an exploration suggestion worthwhile
    condition: (context) => context.singletonCount > 5,
    buildPrompt: buildRhymeExplorationPrompt
  },
  {
    id: 'stability_alignment',
    name: 'Stability Alignment Check',
    priority: 2,
    cooldownMs: 900000, // 15 minutes
    condition: (context) => {
      // Only trigger if both lyrics and stability level are present
      return !!(
        context.lyrics &&
        context.lyrics.trim().length > 50 &&
        context.stabilityLevel !== undefined
      )
    },
    buildPrompt: buildStabilityAlignmentPrompt
  },

  // Priority 3: Medium-value exploratory feedback
  {
    id: 'pattern_analysis',
    name: 'Pattern Analysis Opportunity',
    priority: 3,
    cooldownMs: 1200000, // 20 minutes
    condition: (context) => context.totalWordCount > 10,
    buildPrompt: buildPatternAnalysisPrompt
  },

  // Priority 4: Lower-priority motivational feedback
  {
    id: 'exploration_momentum',
    name: 'Exploration Momentum',
    priority: 4,
    cooldownMs: 1800000, // 30 minutes
    condition: (context) => {
      // Check for recent activity (last 10 minutes)
      if (!context.lastActivity) return false
      const tenMinutesAgo = new Date(Date.now() - 600000)
      return context.lastActivity > tenMinutesAgo && context.explorationDepth > 3
    },
    buildPrompt: buildExplorationMomentumPrompt
  },

  // Priority 5: Fallback generic suggestions
  {
    id: 'creative_suggestions',
    name: 'Creative Suggestions',
    priority: 5,
    cooldownMs: 2400000, // 40 minutes
    condition: (context) => context.totalWordCount > 0,
    buildPrompt: buildCreativeSuggestionsPrompt
  }
]

/**
 * Check if a trigger is enabled based on AI feedback preferences.
 *
 * Maps trigger IDs to their corresponding feature flags:
 * - rhyme_exploration → rhyme
 * - stability_alignment → prosody
 * - pattern_analysis → structure
 * - exploration_momentum → structure
 * - creative_suggestions → always enabled (fallback)
 *
 * @param triggerId - The trigger ID to check
 * @param context - Current word collection AI context
 * @returns true if trigger is enabled, false otherwise
 */
function isTriggerEnabled(triggerId: string, context: WordCollectionAIContext): boolean {
  const enabledFeatures = context.aiFeedbackPreferences?.enabled_features

  // If no preferences provided, all features are enabled by default
  if (!enabledFeatures) return true

  // Map trigger IDs to feature flags
  switch (triggerId) {
    case 'rhyme_exploration':
      return enabledFeatures.rhyme ?? true
    case 'stability_alignment':
      return enabledFeatures.prosody ?? true
    case 'pattern_analysis':
    case 'exploration_momentum':
      return enabledFeatures.structure ?? true
    case 'creative_suggestions':
      // Fallback suggestions always enabled
      return true
    default:
      // Unknown triggers default to enabled
      return true
  }
}

/**
 * Find highest-priority active trigger for current context.
 *
 * Evaluates all triggers in priority order, returning the first
 * one whose condition evaluates to true and whose feature is enabled.
 *
 * @param context - Current word collection AI context
 * @param cooldownState - Map of trigger ID -> last triggered timestamp
 * @returns Active trigger or null if none
 */
export function findActiveTrigger(
  context: WordCollectionAIContext,
  cooldownState: Map<string, number>
): FeedbackTrigger | null {
  const now = Date.now()

  // Sort by priority (ascending - 1 is highest)
  const sortedTriggers = [...WORD_COLLECTION_TRIGGERS].sort(
    (a, b) => a.priority - b.priority
  )

  for (const trigger of sortedTriggers) {
    // Check if trigger's feature is enabled
    if (!isTriggerEnabled(trigger.id, context)) continue

    // Check if condition is met
    if (!trigger.condition(context)) continue

    // Check if cooldown has expired
    const lastTriggered = cooldownState.get(trigger.id)
    if (lastTriggered) {
      const timeSinceLast = now - lastTriggered
      if (timeSinceLast < trigger.cooldownMs) continue
    }

    // This trigger is active and ready to fire
    return trigger
  }

  return null
}
